import { ButtonGroup, IconButton, Menu, MenuItem } from "@mui/material";
import { OLButton } from "OLButton";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useContextMenu } from "hooks/contextMenu";
import { kebabCase } from "lodash";
import { ReactElement } from "react";
import { ConditionalWrap } from "utilities/ConditionalWrap";
import { useStyles } from "./Subheader.styles";

type MenuItemType = {
  hideCondition?: boolean;
  disableCondition?: boolean;
  onClick: () => void;
  text: string;
};

interface Props {
  "data-test-id"?: string;
  disabled?: boolean;
  disabledInfo?: { name: string; text: string };
  hideArrow?: boolean;
  menuItems: MenuItemType[];
  secondary?: boolean;
  size?: "small" | "medium" | "large";
  text: string;
}

export const SubheaderMenuButton = ({
  "data-test-id": dataTestId,
  disabled,
  disabledInfo,
  hideArrow,
  menuItems,
  secondary,
  size = "medium",
  text,
}: Props) => {
  const classes = useStyles();
  const contextMenu = useContextMenu();

  const nonHiddenConditionsExists = menuItems.some(
    (condition) => condition.hideCondition !== true,
  );

  return (
    <>
      <ConditionalWrap
        condition={!!(disabledInfo && (disabled || !nonHiddenConditionsExists))}
        wrap={(text: ReactElement) => (
          <Tooltip
            text={
              <ExternalLinkTooltipContent
                name={disabledInfo?.name as string}
                text={disabledInfo?.text as string}
              />
            }
          >
            {text}
          </Tooltip>
        )}
      >
        <ButtonGroup
          className={classes.buttonGroup}
          data-test-id={dataTestId && `${dataTestId}-${kebabCase(text)}-button`}
          onClick={contextMenu.handleMenuOpen}
        >
          <OLButton
            className={`${classes.buttonGroupButton} ${hideArrow || secondary ? classes.hiddenArrowButton : ""} ${hideArrow && disabled ? classes.hiddenArrowDisabledButton : ""}`}
            data-sentry-unmask
            disabled={disabled || !nonHiddenConditionsExists}
            size={size}
            variant="contained"
          >
            {text}
          </OLButton>
          {!disabled && nonHiddenConditionsExists && !hideArrow && (
            <IconButton
              className={`${classes.buttonGroupIcon} ${secondary ? classes.whiteButtonGroupIcon : ""}`}
            >
              <ChevronDownIcon />
            </IconButton>
          )}
        </ButtonGroup>
      </ConditionalWrap>
      <Menu
        anchorEl={contextMenu.anchor}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        className={classes.menu}
        onClose={contextMenu.handleMenuClose}
        open={contextMenu.isMenuOpen}
        sx={{ top: "8px" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        {menuItems.map((item: MenuItemType) => (
          <div key={item.text}>
            {!item.hideCondition && (
              <MenuItem
                className={classes.menuItem}
                data-sentry-unmask
                data-test-id={`${dataTestId}-${kebabCase(item.text)}-menu-item`}
                onClick={() => {
                  contextMenu.handleMenuClose();
                  item.onClick();
                }}
                disabled={item.disableCondition}
              >
                {item.text}
              </MenuItem>
            )}
          </div>
        ))}
      </Menu>
    </>
  );
};
